import React, { useState, useEffect, useRef, Suspense, useCallback  } from 'react';
import IASideBar from './IASideBar';
import * as mui from '@mui/material';
import ResponsiveContainer from '../../layout/Container';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import BugReportIcon from '@mui/icons-material/BugReport';
import DynamicDrawer from '../../common/Drawer';
import CustomSpeedDial from '../../common/SpeedDial';
import NormalTextField from '../../common/NormalTextField';
import MultipleSelect from '../../common/MultipleSelect';
import DynamicSnackbar from '../../common/Snackbar';
import DataTable from '../../common/DataGrid';
import ExpandableDataGrid from '../../common/ExpandableDataGrid';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileUploadRisks from '../../common/FileUploadRisks';
import Modal from '../../common/Modal';
import LoadingButton from '@mui/lab/LoadingButton';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import auditService from '../../../services/AuditService';
import CircularProgress from '@mui/material/CircularProgress';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import userService from '../../../services/UserService';
import companyService from '../../../services/CompanyService';

const AuditRiskLibrary = () => {
  
    const [users, setUsers] = useState([]);
    const [apps, setApps] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [error, setError] = useState(null);
    const [open, setOpen] = useState(false);
    const [riskData, setRiskData] = useState([]);
    const [newData, setNewData] = useState([]);
    const [riskType, setRiskType] = useState([]);
    const [riskCategory, setRiskCategory] = useState([]);
    const saveTimeout = useRef(null);
    const [riskExist, setRiskExist] = useState([]);

    const [anchorEl, setAnchorEl] = useState(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editingRisk, setEditingRisk] = useState(null);
    const [editRiskType, setEditRiskType] = useState(null);
    const [editRiskCategory, setEditRiskCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [editErrors, setEditErrors] = useState({
        RISK_ID: '',
        RISK_NAME: '',
        RISK_TYPE: '',
        RISK_DESCRIPTION: ''
    });
    const [editFormValid, setEditFormValid] = useState(false);
    const [editIsLoading, setEditIsLoading] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [riskToDelete, setRiskToDelete] = useState(null);
    const [deleteIsLoading, setDeleteIsLoading] = useState(false);
    const [dependencyInfo, setDependencyInfo] = useState(null);

    const [openRisksModal, setOpenRisksModal] = useState(false);
    const [risksUpload, setRisksUpload] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploadComplete, setIsUploadComplete] = useState(false);
    
    const [errors, setErrors] = useState({
        RISK_ID: '',
        RISK_NAME: '',
        RISK_TYPE: '',
        RISK_DESCRIPTION: ''
    });
    const [formValid, setFormValid] = useState(false);

    const handleClick = (event, app) => {
        // Find the full risk object from riskData based on RISK_ID and appID (which stores the UUID)
        const riskToEdit = riskData.find(risk => risk.id === app.appID);
        if (!riskToEdit) return;
        
        // Set up the editing state
        setEditingRisk({...riskToEdit});
        
        // Set up the risk type dropdown
        if (riskToEdit.RISK_TYPE) {
            const matchedType = riskTypeOptions.find(opt => opt.value === riskToEdit.RISK_TYPE);
            setEditRiskType(matchedType || null);
        } else {
            setEditRiskType(null);
        }
        
        // Set up the risk category dropdown
        if (riskToEdit.REQUIRED) {
            const matchedCategory = riskCategoryOptions.find(opt => opt.value === riskToEdit.REQUIRED);
            setEditRiskCategory(matchedCategory || null);
        } else {
            setEditRiskCategory(null);
        }
        
        // Clear any errors and set form as valid initially
        setEditErrors({
            RISK_ID: '',
            RISK_NAME: '',
            RISK_TYPE: '',
            RISK_DESCRIPTION: ''
        });
        setEditFormValid(true);
        
        // Open the edit modal
        setEditModalOpen(true);
    };

    const toggleDrawer = (open) => () => {
        if (open) {
            // Initialize form fields when opening the drawer
            setNewData({});
            setRiskType(null);
            setRiskCategory(null);
            setError(null);
            setErrors({
                RISK_ID: '',
                RISK_NAME: '',
                RISK_TYPE: '',
                RISK_DESCRIPTION: '',
                REQUIRED: ''
            });
            setFormValid(false);
        } else {
            // Clear form fields when closing the drawer
            setNewData({});
            setRiskType(null);
            setRiskCategory(null);
            setError(null);
            setErrors({
                RISK_ID: '',
                RISK_NAME: '',
                RISK_TYPE: '',
                RISK_DESCRIPTION: '',
                REQUIRED: ''
            });
            setFormValid(false);
        }
        setOpen(open);
    };

    const renderLoadingState = () => (
        <mui.Box 
            sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '80vh',
                backgroundColor: 'whitesmoke'
            }}
        >
            <mui.Box sx={{ textAlign: 'center' }}>
                <CircularProgress size={60} thickness={4} />
                <mui.Typography 
                    variant="h6" 
                    sx={{ 
                        mt: 2, 
                        color: 'text.secondary',
                        fontWeight: 500
                    }}
                >
                    Loading Risk Library...
                </mui.Typography>
            </mui.Box>
        </mui.Box>
    );

    useEffect(() => {
        const fetchUserAndData = async () => {
            try {
                setIsLoading(true);
                // Fetch current user
                const user = await userService.fetchCurrentUser();
                setCurrentUser(user);
                
                // Fetch company data if needed
                if (user && user.company) {
                    const companyData = await companyService.fetchCompanyById(user.company);
                    if (companyData) {
                        setCompanies([companyData]);
                    }
                }
                
                // Fetch risk data
                const riskList = await auditService.fetchRisk();
                if (riskList) {
                    setRiskData(riskList);
                    setRiskExist(riskList);
                }
            } catch (fetchError) {
                console.error('Error fetching data:', fetchError);
                setRiskData([]); 
                setRiskExist([]);
            } finally {
                // Add a minimum delay of 1 second before hiding the loading state
                setTimeout(() => {
                    setIsLoading(false);
                }, 1000);
            }
        };
        
        fetchUserAndData();
    }, []); 

    useEffect(() => {
        const isExistingRisk = riskExist.some(risk => risk.RISK_ID === riskData.RISK_ID);
        if (isExistingRisk) {
            setError('Risk ID already exists');
        } else {
            setError('');
        }
    }, [riskData]); 

    // Validate form fields and enable/disable submit button
    useEffect(() => {
        // Check if all required fields are filled and valid
        const validRiskId = newData.RISK_ID && newData.RISK_ID.trim() !== '' && !errors.RISK_ID;
        const validRiskName = newData.RISK_NAME && newData.RISK_NAME.trim() !== '' && !errors.RISK_NAME;
        const validRiskType = newData.RISK_TYPE && !errors.RISK_TYPE;
        const validRiskDescription = newData.RISK_DESCRIPTION && newData.RISK_DESCRIPTION.trim() !== '' && !errors.RISK_DESCRIPTION;
        
        // Set form validity state without requiring REQUIRED field
        setFormValid(validRiskId && validRiskName && validRiskType && validRiskDescription);
    }, [newData, errors]);
   
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    const columns = [
        { field: 'id', headerName: '#', width: 50 },
        { field: 'RISK_ID', headerName: 'Risk ID', flex: .2 },
        { field: 'RISK_NAME', headerName: 'Risk Name', flex: .2 },
        { field: 'RISK_TYPE', headerName: 'Risk Type', flex: .2 },
        { field: 'RISK_DESCRIPTION', headerName: 'Risk Description', flex: 1 },

    ];

    // Create the initial rows from risk data
    const initialRows = Array.isArray(riskData) ? riskData.map((risk, index) => ({
        id: index + 1,
        RISK_ID: risk.RISK_ID || '-',
        RISK_NAME: risk.RISK_NAME || '-',
        RISK_TYPE: risk.RISK_TYPE || '-',
        RISK_DESCRIPTION: risk.RISK_DESCRIPTION || '-',
        appID: risk.id,
    })) : [];

    // Filter rows based on search query
    const rows = searchQuery.trim() === '' ? initialRows : initialRows.filter(row => {
        const searchLower = searchQuery.toLowerCase();
        return (
            (row.RISK_ID && row.RISK_ID.toLowerCase().includes(searchLower)) ||
            (row.RISK_NAME && row.RISK_NAME.toLowerCase().includes(searchLower)) ||
            (row.RISK_TYPE && row.RISK_TYPE.toLowerCase().includes(searchLower)) ||
            (row.RISK_DESCRIPTION && row.RISK_DESCRIPTION.toLowerCase().includes(searchLower))
        );
    });
    
   
    const renderAuditPrepButton = (params) => {
        const app = params.row;
        const readyforaudit = app.geninfo && app.userdata && app.process;

        return (
            <mui.Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Edit Risk" arrow>
                    <mui.IconButton
                        size="small"
                        onClick={(event) => handleClick(event, app)}
                    >
                    <EditIcon sx={{fontSize: '18px'}} />
                    </mui.IconButton>
                </Tooltip>
                <Tooltip title="Delete Risk" arrow>
                    <mui.IconButton
                        size="small"
                        color="default"
                        onClick={() => handleDeleteClick(app)}
                        sx={{
                            color: 'rgba(0, 0, 0, 0.54)',
                            '&:hover': {
                                color: 'rgba(211, 47, 47, 0.9)',
                                backgroundColor: 'rgba(211, 47, 47, 0.08)'
                            }
                        }}
                    >
                        <DeleteIcon sx={{fontSize: '18px'}} />
                    </mui.IconButton>
                </Tooltip>
            </mui.Box>
        );
    };

    const columnsWithActions = [
        ...columns,
        {
            field: 'actions',
            headerName: 'Modify',
            width: 200,
            sortable: false,
            renderCell: renderAuditPrepButton,
        },
    ];
    
    const createRisk = async (e) => {
        e.preventDefault();

        // Don't proceed if the form is not valid
        if (!formValid) {
            setSnackbarMessage('Please fill all required fields correctly');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        try {
            setIsLoading(true);
            
            // Add company ID to the risk data if available
            const riskDataWithCompany = {
                ...newData,
                COMPANY_ID: currentUser?.company || null
            };
            
            const newrisk = await auditService.createRisk(riskDataWithCompany);
            if (newrisk) {
                setIsLoading(false);
                setSnackbarMessage('Successfully created risk');
                setSnackbarSeverity('success');
                setSnackbarOpen(true);

                setTimeout(() => {
                    window.location.reload(); // Reload the page
                }, 1000);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error creating risk record:', error);
            setSnackbarMessage('There was a problem creating risk');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const riskTypeOptions = [
        { value: 'Business Process', label: 'Business Process' },
        { value: 'IT', label: 'IT' },
        { value: 'Operational', label: 'Operational' },
        { value: 'Entity Level', label: 'Entity Level' },
    ];

    const riskCategoryOptions = [
        { value: 'Yes', label: 'Yes' },
        { value: 'No', label: 'No' },
    ];

    const handleRiskTypeChange = (selectedType) => {
        if (!selectedType) {
            setErrors(prev => ({
                ...prev,
                RISK_TYPE: 'Risk Type is required'
            }));
            return;
        }
        
        const risktype = selectedType.value;
        setRiskType(selectedType);

        const updatedData = {
            ...newData,
            RISK_TYPE: risktype
        };

        setNewData(updatedData);
        
        setErrors(prev => ({
            ...prev,
            RISK_TYPE: ''
        }));
    };

    const handleRiskCategoryChange = (selectedType) => {
        if (!selectedType) {
            setErrors(prev => ({
                ...prev,
                REQUIRED: 'Relevance selection is required'
            }));
            return;
        }

        const riskcategory = selectedType.value;
        setRiskCategory(selectedType);

        const updatedData = {
            ...newData,
            REQUIRED: riskcategory
        };

        setNewData(updatedData);
        
        setErrors(prev => ({
            ...prev,
            REQUIRED: ''
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (saveTimeout.current) {
            clearTimeout(saveTimeout.current);
        }
        
        const updatedData = {
            ...newData,
            [name]: value
        };

        setNewData(updatedData);

        // Validate field as user types
        validateField(name, value);
    };

    const validateField = (name, value) => {
        let errorMessage = '';
        
        switch (name) {
            case 'RISK_ID':
                if (!value.trim()) {
                    errorMessage = 'Risk ID is required';
                } else if (riskExist.some(risk => risk.RISK_ID === value)) {
                    errorMessage = 'Risk ID already exists';
                } else if (value.length < 3) {
                    errorMessage = 'Risk ID must be at least 3 characters';
                }
                break;
            case 'RISK_NAME':
                if (!value.trim()) {
                    errorMessage = 'Risk Name is required';
                } else if (value.length < 5) {
                    errorMessage = 'Risk Name must be at least 5 characters';
                }
                break;
            case 'RISK_DESCRIPTION':
                if (!value.trim()) {
                    errorMessage = 'Risk Description is required';
                } else if (value.length < 10) {
                    errorMessage = 'Description must be at least 10 characters';
                }
                break;
            default:
                break;
        }

        setErrors(prev => ({
            ...prev,
            [name]: errorMessage
        }));
    };

    const handleRiskBlur = (e) => {
        const { name, value } = e.target;

        const updatedData = {
            ...newData,
            [name]: value
        };
 
        setNewData(updatedData);
        validateField(name, value);
    };

    const handleOpenRisksModal = () => {
        setOpenRisksModal(true);
    };

    const handleCloseRisksModal = (event, reason) => {
        // Only prevent default closing if reason is backdropClick
        if (reason === 'backdropClick') {
            return;
        }
        // For any other reason, close the modal
        setOpenRisksModal(false);
        
        // Reset upload states when closing the modal
        setRisksUpload([]);
        setIsUploadComplete(false);
    };

    const handleOnRisksData = (data) => {
        console.log('Received data from FileUploadRisks:', data);
        if (Array.isArray(data) && data.length > 0) {
            // Check for duplicate RISK_IDs within the uploaded data
            const riskIds = data.map(risk => risk.RISK_ID);
            const uniqueRiskIds = [...new Set(riskIds)];
            
            if (uniqueRiskIds.length !== riskIds.length) {
                // Find the duplicate IDs
                const counts = {};
                const duplicates = [];
                riskIds.forEach(id => {
                    counts[id] = (counts[id] || 0) + 1;
                    if (counts[id] > 1 && !duplicates.includes(id)) {
                        duplicates.push(id);
                    }
                });
                
                setSnackbarMessage(`Duplicate Risk IDs found in upload file: ${duplicates.join(', ')}. Please fix and try again.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            // Check for duplicate RISK_IDs against existing records
            const existingDuplicates = [];
            for (const risk of data) {
                if (riskExist.some(existingRisk => existingRisk.RISK_ID === risk.RISK_ID)) {
                    existingDuplicates.push(risk.RISK_ID);
                }
            }
            
            if (existingDuplicates.length > 0) {
                setSnackbarMessage(`These Risk IDs already exist in the database: ${existingDuplicates.join(', ')}. Please fix and try again.`);
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                return;
            }
            
            setRisksUpload(data);
            setIsUploadComplete(false);
            
            // Show notification when data is loaded
            setSnackbarMessage(`Loaded ${data.length} risks ready to upload`);
            setSnackbarSeverity('info');
            setSnackbarOpen(true);
        } else {
            setRisksUpload([]);
            console.warn('Received empty or invalid data from file upload');
        }
    };

    // Function to download CSV template
    const downloadTemplate = () => {
        // Create template CSV content - headers match exactly with the RISKLIST model fields
        // Note that REQUIRED is now optional
        const csvContent = [
            "RISK_ID,RISK_NAME,RISK_TYPE,RISK_DESCRIPTION,REQUIRED",
            "RISK001,Unauthorized Access,IT,Risk of unauthorized access to sensitive systems,Yes",
            "RISK002,Data Loss,IT,Risk of data loss or leakage,Yes",
            "RISK003,Business Disruption,Business Process,Risk of critical business process disruption,No"
        ].join("\n");
        
        // Create a blob from the CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        
        // Create a temporary link to trigger download
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "RiskLibrary_Template.csv");
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url); // Clean up the URL object
    };

    const processUploadsWithCompanyID = (uploads) => {
        if (!Array.isArray(uploads) || uploads.length === 0) {
            return uploads;
        }
        
        // Add company ID to each risk in the upload array if it doesn't already have one
        return uploads.map(risk => {
            if (!risk.COMPANY_ID && currentUser?.company) {
                return {
                    ...risk,
                    COMPANY_ID: currentUser.company
                };
            }
            return risk;
        });
    };

    const handleButtonClick = async () => {
        if (!risksUpload || risksUpload.length === 0) {
            setSnackbarMessage('No data to upload');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setIsLoading(true);
        setIsUploadComplete(false);

        try {
            // Process uploads to ensure they have company ID
            const processedUploads = processUploadsWithCompanyID(risksUpload);
            console.log('Uploading risks data:', processedUploads);
            
            // Track success and failures
            let successCount = 0;
            let failureCount = 0;
            let duplicateRiskIds = [];
            
            // Use individual createRisk calls instead of createRiskMapping
            const results = await Promise.allSettled(
                processedUploads.map(risk => auditService.createRisk(risk))
            );
            
            // Count successes and failures
            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    successCount++;
                } else {
                    failureCount++;
                    console.error("Error uploading risk:", result.reason);
                    
                    // Check for duplicate RISK_ID error messages
                    const errorMessage = result.reason?.response?.data?.detail || '';
                    if (errorMessage.includes('already exists')) {
                        duplicateRiskIds.push(processedUploads[index].RISK_ID);
                    }
                }
            });
            
            // Refresh risk data
            const updatedRiskList = await auditService.fetchRisk();
            if (updatedRiskList) {
                setRiskData(updatedRiskList);
                setRiskExist(updatedRiskList);
            }
            
            setIsLoading(false);
            setIsUploadComplete(true);
            
            let message;
            let severity;
            
            if (duplicateRiskIds.length > 0) {
                message = `Upload partially completed: ${successCount} risks added, ${duplicateRiskIds.length} failed due to duplicate Risk IDs: ${duplicateRiskIds.join(', ')}`;
                severity = 'warning';
            } else if (failureCount > 0) {
                message = `Upload partially completed: ${successCount} risks added, ${failureCount} failed due to errors`;
                severity = 'warning';
            } else {
                message = `Successfully uploaded ${successCount} risks`;
                severity = 'success';
            }
                
            setSnackbarMessage(message);
            setSnackbarSeverity(severity);
            setSnackbarOpen(true);

            // Only refresh the page after a successful upload
            if (failureCount === 0) {
                // Show success state briefly, then refresh the page
                setTimeout(() => {
                    window.location.reload(); // Refresh the page
                }, 1500);
            }
        } catch (error) {
            console.error('Error uploading risks:', error);
            setIsLoading(false);
            setIsUploadComplete(false);
            
            // Check for duplicate RISK_ID error in the main error
            const errorDetail = error.response?.data?.detail || '';
            const errorMessage = errorDetail.includes('already exist') 
                ? errorDetail 
                : 'Error uploading risks: ' + (error.message || 'Unknown error');
            
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    // Handle edit modal close
    const handleEditModalClose = () => {
        setEditModalOpen(false);
        setEditingRisk(null);
        setEditRiskType(null);
        setEditRiskCategory(null);
    };
    
    // Handle change in edit form fields
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        
        setEditingRisk(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Validate the field
        validateEditField(name, value);
    };
    
    // Validate edit form fields
    const validateEditField = (name, value) => {
        let errorMessage = '';
        
        switch (name) {
            case 'RISK_ID':
                if (!value.trim()) {
                    errorMessage = 'Risk ID is required';
                } else if (riskExist.some(risk => risk.RISK_ID === value && risk.RISK_ID !== editingRisk.RISK_ID)) {
                    errorMessage = 'Risk ID already exists';
                } else if (value.length < 3) {
                    errorMessage = 'Risk ID must be at least 3 characters';
                }
                break;
            case 'RISK_NAME':
                if (!value.trim()) {
                    errorMessage = 'Risk Name is required';
                } else if (value.length < 5) {
                    errorMessage = 'Risk Name must be at least 5 characters';
                }
                break;
            case 'RISK_DESCRIPTION':
                if (!value.trim()) {
                    errorMessage = 'Risk Description is required';
                } else if (value.length < 10) {
                    errorMessage = 'Description must be at least 10 characters';
                }
                break;
            default:
                break;
        }
        
        setEditErrors(prev => ({
            ...prev,
            [name]: errorMessage
        }));
    };
    
    // Handle edit risk type change
    const handleEditRiskTypeChange = (selectedType) => {
        if (!selectedType) {
            setEditErrors(prev => ({
                ...prev,
                RISK_TYPE: 'Risk Type is required'
            }));
            return;
        }
        
        setEditRiskType(selectedType);
        setEditingRisk(prev => ({
            ...prev,
            RISK_TYPE: selectedType.value
        }));
        
        setEditErrors(prev => ({
            ...prev,
            RISK_TYPE: ''
        }));
    };
    
    // Handle edit risk category change
    const handleEditRiskCategoryChange = (selectedType) => {
        if (!selectedType) {
            setEditErrors(prev => ({
                ...prev,
                REQUIRED: 'Relevance selection is required'
            }));
            return;
        }
        
        setEditRiskCategory(selectedType);
        setEditingRisk(prev => ({
            ...prev,
            REQUIRED: selectedType.value
        }));
        
        setEditErrors(prev => ({
            ...prev,
            REQUIRED: ''
        }));
    };
    
    // Validate edit form
    useEffect(() => {
        if (!editingRisk) {
            setEditFormValid(false);
            return;
        }
        
        // Check if all required fields are filled and valid
        const validRiskId = editingRisk.RISK_ID && editingRisk.RISK_ID.trim() !== '' && !editErrors.RISK_ID;
        const validRiskName = editingRisk.RISK_NAME && editingRisk.RISK_NAME.trim() !== '' && !editErrors.RISK_NAME;
        const validRiskType = editingRisk.RISK_TYPE && !editErrors.RISK_TYPE;
        const validRiskDescription = editingRisk.RISK_DESCRIPTION && editingRisk.RISK_DESCRIPTION.trim() !== '' && !editErrors.RISK_DESCRIPTION;
        
        // Set form validity state without requiring REQUIRED field
        setEditFormValid(validRiskId && validRiskName && validRiskType && validRiskDescription);
    }, [editingRisk, editErrors]);
    
    // Update the risk
    const updateRisk = async () => {
        if (!editFormValid || !editingRisk) {
            setSnackbarMessage('Please fill all required fields correctly');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }
        
        // Double-check for duplicate RISK_ID before submitting
        if (riskExist.some(risk => 
            risk.RISK_ID === editingRisk.RISK_ID && 
            risk.id !== editingRisk.id
        )) {
            setSnackbarMessage(`Risk ID '${editingRisk.RISK_ID}' already exists`);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            
            // Update the error state
            setEditErrors(prev => ({
                ...prev,
                RISK_ID: 'Risk ID already exists'
            }));
            
            return;
        }
        
        try {
            setEditIsLoading(true);
            
            // Clone the risk data to avoid modifying the state directly
            const riskData = { ...editingRisk };
            
            console.log('Updating risk with data:', riskData);
            
            // In Django, foreign keys need to be sent as just the ID, not the full object
            if (currentUser?.company) {
                if (typeof riskData.COMPANY_ID === 'object' && riskData.COMPANY_ID !== null) {
                    // If COMPANY_ID is already an object with an id property, use that
                    riskData.COMPANY_ID = riskData.COMPANY_ID.id || currentUser.company;
                } else if (!riskData.COMPANY_ID) {
                    // If COMPANY_ID is not set, use the current user's company
                    riskData.COMPANY_ID = currentUser.company;
                }
                // If it's already a string/ID, leave it as is
            }
            
            // Use the risk's id field for the update
            const riskId = riskData.id || '';
            const updatedRisk = await auditService.updateRisk(riskId, riskData);
            handleSuccessfulUpdate(updatedRisk);
            
        } catch (error) {
            setEditIsLoading(false);
            console.error('Error updating risk record:', error);
            
            // Check if the error is due to duplicate RISK_ID
            const errorDetail = error.response?.data?.detail || '';
            if (errorDetail.includes('already exists') || errorDetail.includes('duplicate')) {
                setSnackbarMessage(`Risk ID '${editingRisk.RISK_ID}' already exists`);
                
                // Update the error state
                setEditErrors(prev => ({
                    ...prev,
                    RISK_ID: 'Risk ID already exists'
                }));
            } else {
                setSnackbarMessage('There was a problem updating risk: ' + (errorDetail || error.message));
            }
            
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };
    
    // Helper function to handle a successful update
    const handleSuccessfulUpdate = async (updatedRisk) => {
        // Refresh the risk data
        const updatedRiskList = await auditService.fetchRisk();
        if (updatedRiskList) {
            setRiskData(updatedRiskList);
            setRiskExist(updatedRiskList);
        }
        
        setEditIsLoading(false);
        handleEditModalClose();
        
        setSnackbarMessage('Successfully updated risk');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleDeleteClick = (app) => {
        // Reset any previous dependency information
        setDependencyInfo(null);
        
        // Find the full risk object from riskData based on RISK_ID and appID
        const riskToDelete = riskData.find(risk => risk.id === app.appID);
        if (!riskToDelete) return;
        
        // Set the risk to delete and open confirmation dialog
        setRiskToDelete(riskToDelete);
        setDeleteConfirmOpen(true);
    };
    
    const handleDeleteConfirmClose = () => {
        setDeleteConfirmOpen(false);
        setRiskToDelete(null);
    };
    
    const handleDeleteConfirm = async () => {
        if (!riskToDelete || !riskToDelete.id) {
            setSnackbarMessage('Cannot delete: Missing risk ID');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleDeleteConfirmClose();
            return;
        }
        
        try {
            setDeleteIsLoading(true);
            
            // Call the deleteRisk API with the risk UUID
            await auditService.deleteRisk(riskToDelete.id);
            
            // Refresh the risk data after deletion
            const updatedRiskList = await auditService.fetchRisk();
            if (updatedRiskList) {
                setRiskData(updatedRiskList);
                setRiskExist(updatedRiskList);
            }
            
            setDeleteIsLoading(false);
            handleDeleteConfirmClose();
            
            setSnackbarMessage('Successfully deleted risk');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            setDeleteIsLoading(false);
            console.error('Error deleting risk record:', error);
            
            // Check for foreign key constraint error
            const errorMessage = error.response?.data?.detail || error.message || '';
            const errorStatus = error.response?.status;
            
            // Handle foreign key constraint violations (often returned as 500 errors)
            if (
                errorStatus === 500 || 
                errorStatus === 409 || 
                errorMessage.includes('foreign key constraint fails') || 
                errorMessage.includes('IntegrityError') ||
                errorMessage.includes('Cannot delete or update a parent row')
            ) {
                // Try to extract table name from the error message for better guidance
                let affectedTable = '';
                if (errorMessage.includes('CONSTRAINT `')) {
                    const constraintMatch = errorMessage.match(/CONSTRAINT `([^`]+)`/);
                    if (constraintMatch && constraintMatch[1]) {
                        affectedTable = constraintMatch[1].split('_')[0]; // Get the first part of the constraint name
                    }
                }
                
                if (errorMessage.includes('auditfile')) {
                    affectedTable = 'Audit Files';
                } else if (errorMessage.includes('AUDITFILE')) {
                    affectedTable = 'Audit Files';
                }
                
                // Set dependency info for UI display
                setDependencyInfo({
                    riskId: riskToDelete?.RISK_ID || '',
                    riskName: riskToDelete?.RISK_NAME || '',
                    affectedTable: affectedTable || 'audits'
                });
                
                setSnackbarMessage(
                    'This risk cannot be deleted because it is being used in one or more audits. ' +
                    'Please remove all references to this risk before deleting it.'
                );
            } else {
                setDependencyInfo(null);
                setSnackbarMessage('There was a problem deleting risk: ' + errorMessage);
            }
            
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            handleDeleteConfirmClose();
        }
    };

    // Function to download the Risk Library as CSV
    const downloadRiskLibrary = () => {
        if (!initialRows || initialRows.length === 0) {
            setSnackbarMessage('No risks data available to download');
            setSnackbarSeverity('warning');
            setSnackbarOpen(true);
            return;
        }

        try {
            // Define the CSV headers based on the columns
            const headers = [
                'Risk ID',
                'Risk Name',
                'Risk Type',
                'Risk Description'
            ].join(',');

            // Map the rows to CSV format
            const csvRows = initialRows.map(row => {
                // Properly escape fields that might contain commas, quotes, or newlines
                const escapeCsvField = (field) => {
                    if (field === null || field === undefined || field === '-') return '';
                    const stringField = String(field);
                    // If the field contains quotes, commas, or newlines, enclose in quotes and escape quotes
                    if (stringField.includes('"') || stringField.includes(',') || stringField.includes('\n')) {
                        return `"${stringField.replace(/"/g, '""')}"`;
                    }
                    return stringField;
                };

                return [
                    escapeCsvField(row.RISK_ID),
                    escapeCsvField(row.RISK_NAME),
                    escapeCsvField(row.RISK_TYPE),
                    escapeCsvField(row.RISK_DESCRIPTION)
                ].join(',');
            });

            // Combine headers and rows
            const csvContent = [headers, ...csvRows].join('\n');
            
            // Create a blob from the CSV content
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            
            // Create a temporary link to trigger download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Risk_Library_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            
            // Clean up
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            // Show success message
            setSnackbarMessage('Risk Library downloaded successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Error downloading Risk Library:', error);
            setSnackbarMessage('Failed to download Risk Library');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        }
    };

    const customMainContent = (
        <div>
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                    <mui.Breadcrumbs 
                        aria-label="breadcrumb"
                        sx={{
                            '& .MuiLink-root': {
                                display: 'flex',
                                alignItems: 'center',
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                transition: 'color 0.2s ease',
                                '&:hover': {
                                    color: 'primary.main'
                                }
                            }
                        }}
                    >
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            <i className="material-icons">home</i>
                        </mui.Link>
                        <mui.Link underline="hover" color="inherit" href="/dashboard">
                            My Dashboard
                        </mui.Link>
                        <mui.Typography color="text.primary">Risk Library</mui.Typography>
                    </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 4, mt: 3 }}>
                        <WarningAmberRoundedIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
                        <mui.Box>
                            <mui.Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                Risk Library
                            </mui.Typography>
                            <mui.Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Manage and document risks that are relevant to the organization. Track and assess potential risks across your audit activities.
                            </mui.Typography>
                        </mui.Box>
                    </mui.Box>

                    {isLoading ? (
                        <mui.Box 
                            sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                alignItems: 'center', 
                                height: '60vh',
                                backgroundColor: '#f8fafc',
                                borderRadius: 3,
                                boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                            }}
                        >
                            <mui.Box sx={{ textAlign: 'center' }}>
                                <CircularProgress 
                                    size={40} 
                                    thickness={4}
                                    sx={{
                                        color: 'primary.main',
                                        animation: 'pulse 1.5s ease-in-out infinite',
                                    }}
                                />
                                <mui.Typography 
                                    variant="body1" 
                                    sx={{ 
                                        mt: 2, 
                                        color: 'text.secondary',
                                        fontWeight: 500
                                    }}
                                >
                                    Loading Risk Library...
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    ) : (
                        <mui.Box sx={{ 
                            backgroundColor: '#f8fafc', 
                            borderRadius: 3,
                            minHeight: '80vh',
                            boxShadow: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)'
                        }}>
                            <mui.Paper 
                                elevation={0}
                                sx={{ 
                                    p: 3,
                                    height: '800px',
                                    backgroundColor: 'transparent'
                                }}
                            >
                                {/* Enhanced Search Component */}
                                <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <mui.Paper
                                        elevation={0}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            p: 0.5,
                                            pl: 2,
                                            borderRadius: '12px',
                                            backgroundColor: '#fff',
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
                                            transition: 'all 0.3s ease',
                                            border: '1px solid #f0f0f0',
                                            '&:hover': {
                                                boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                                borderColor: 'rgba(25, 118, 210, 0.2)'
                                            },
                                            maxWidth: '380px',
                                            position: 'relative'
                                        }}
                                    >
                                        <mui.Box 
                                            component="span" 
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                color: theme => searchQuery ? theme.palette.primary.main : 'text.secondary',
                                                transition: 'color 0.2s ease'
                                            }}
                                        >
                                            <i className="material-icons" style={{ fontSize: '20px' }}>search</i>
                                        </mui.Box>
                                        <mui.InputBase
                                            placeholder="Search risks..."
                                            value={searchQuery}
                                            onChange={handleSearchChange}
                                            sx={{
                                                ml: 1.5,
                                                flex: 1,
                                                fontSize: '0.95rem',
                                                py: 1,
                                                '& .MuiInputBase-input': {
                                                    transition: 'all 0.2s ease',
                                                    '&::placeholder': {
                                                        opacity: 0.7,
                                                        transition: 'opacity 0.2s ease'
                                                    },
                                                    '&:focus::placeholder': {
                                                        opacity: 0.5
                                                    }
                                                }
                                            }}
                                        />
                                        {searchQuery && (
                                            <mui.Fade in={!!searchQuery}>
                                                <mui.IconButton
                                                    size="small"
                                                    aria-label="clear search"
                                                    onClick={() => setSearchQuery('')}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        mr: 0.5,
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            color: 'primary.main',
                                                            backgroundColor: 'rgba(25, 118, 210, 0.08)'
                                                        },
                                                        opacity: 0.7
                                                    }}
                                                >
                                                    <i className="material-icons" style={{ fontSize: '18px' }}>close</i>
                                                </mui.IconButton>
                                            </mui.Fade>
                                        )}
                                        {rows.length !== initialRows.length && (
                                            <mui.Chip
                                                label={`${rows.length} of ${initialRows.length}`}
                                                size="small"
                                                sx={{
                                                    height: '22px',
                                                    ml: 1,
                                                    mr: 0.5,
                                                    fontSize: '0.7rem',
                                                    backgroundColor: 'primary.main',
                                                    color: 'white',
                                                    fontWeight: 500,
                                                    '& .MuiChip-label': {
                                                        px: 1
                                                    }
                                                }}
                                            />
                                        )}
                                    </mui.Paper>
                                    
                                    <mui.Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadRiskLibrary}
                                        sx={{
                                            borderRadius: '8px',
                                            textTransform: 'none',
                                            fontSize: '0.875rem',
                                            borderColor: 'rgba(25, 118, 210, 0.12)',
                                            color: 'primary.main',
                                            px: 2,
                                            py: 0.75,
                                            fontWeight: 500,
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                                            '&:hover': {
                                                backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                                borderColor: 'primary.main',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.08)'
                                            }
                                        }}
                                    >
                                        Download Library
                                    </mui.Button>
                                </mui.Box>

                                <ExpandableDataGrid
                                    rows={rows}
                                    columns={columns}
                                    columnsWithActions={columnsWithActions}
                                    height={700}
                                    expandableFields={['RISK_DESCRIPTION']}
                                    sx={{
                                        '& .MuiDataGrid-root': {
                                            border: 'none',
                                            backgroundColor: 'white',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            border: '1px solid',
                                            borderColor: 'rgba(25, 118, 210, 0.08)',
                                            '&:hover': {
                                                borderColor: 'rgba(25, 118, 210, 0.12)'
                                            }
                                        }
                                    }}
                                />
                            </mui.Paper>
                        </mui.Box>
                    )}

                    <DynamicDrawer
                        open={open} 
                        onClose={toggleDrawer(false)}
                    >
                        <mui.Box sx={{ 
                            padding: '24px 20px',
                            width: '420px',
                            maxWidth: '100%',
                            background: '#ffffff',
                            height: '100%',
                            overflowY: 'auto'
                        }}>
                            <mui.Typography variant="h6" sx={{ 
                                mb: 3, 
                                fontWeight: 600, 
                                color: 'text.primary',
                                position: 'relative',
                                '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: -6,
                                    left: 0,
                                    width: '40px',
                                    height: '3px',
                                    backgroundColor: 'primary.main'
                                }
                            }}>
                                Create New Risk
                            </mui.Typography>

                            <mui.Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Risk ID <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <NormalTextField
                                        error={!!errors.RISK_ID}
                                        name="RISK_ID"
                                        placeholder="Enter risk identifier"
                                        value={newData.RISK_ID || ''}
                                        onChange={handleChange}
                                        onBlur={handleRiskBlur}
                                        helperText={errors.RISK_ID || ' '}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                                height: '40px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                }
                                            },
                                            '& .MuiFormHelperText-root': {
                                                mt: 0.5,
                                                fontSize: '0.7rem',
                                                lineHeight: 1.2
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Risk Name <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <NormalTextField
                                        error={!!errors.RISK_NAME}
                                        name="RISK_NAME"
                                        placeholder="Enter descriptive name"
                                        value={newData.RISK_NAME || ''}
                                        onChange={handleChange}
                                        onBlur={handleRiskBlur}
                                        helperText={errors.RISK_NAME || ' '}
                                        fullWidth
                                        size="small"
                                        sx={{
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                                height: '40px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                }
                                            },
                                            '& .MuiFormHelperText-root': {
                                                mt: 0.5,
                                                fontSize: '0.7rem',
                                                lineHeight: 1.2
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Risk Type <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <MultipleSelect
                                        isMultiSelect={false}
                                        defaultValue={riskType ? riskType : ''}
                                        placeholderText="Select risk type"
                                        selectOptions={riskTypeOptions}
                                        selectedOptions={riskType ? riskType : ''}
                                        handleChange={handleRiskTypeChange}
                                        error={!!errors.RISK_TYPE}
                                        customStyles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: '40px',
                                                height: '40px',
                                                fontSize: '0.875rem'
                                            })
                                        }}
                                    />
                                    {errors.RISK_TYPE && (
                                        <mui.Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                            {errors.RISK_TYPE}
                                        </mui.Typography>
                                    )}
                                </mui.Box>

                                <mui.Box sx={{ mb: 2.5 }}>
                                    <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Risk Description <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                    </mui.Typography>
                                    <NormalTextField
                                        error={!!errors.RISK_DESCRIPTION}
                                        name="RISK_DESCRIPTION"
                                        placeholder="Enter detailed description of the risk"
                                        value={newData.RISK_DESCRIPTION || ''}
                                        onChange={handleChange}
                                        onBlur={handleRiskBlur}
                                        helperText={errors.RISK_DESCRIPTION || ' '}
                                        isMultiLine={true}
                                        rows="4"
                                        fullWidth
                                        sx={{
                                            mt: 0,
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '4px',
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.light',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: 'primary.main',
                                                    borderWidth: '1px',
                                                }
                                            },
                                            '& .MuiFormHelperText-root': {
                                                mt: 0.5,
                                                fontSize: '0.7rem',
                                                lineHeight: 1.2
                                            }
                                        }}
                                    />
                                </mui.Box>

                                <mui.Box sx={{ mt: 4 }}>
                                    <mui.Divider sx={{ mb: 2 }} />
                                    <mui.Stack direction="row" spacing={1.5} justifyContent="flex-end">
                                        <mui.Button 
                                            variant="outlined" 
                                            onClick={handleOpenRisksModal}
                                            startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
                                            size="small"
                                            sx={{
                                                py: 1,
                                                px: 2,
                                                borderRadius: '4px',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                borderColor: 'divider',
                                                color: 'text.primary',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                }
                                            }}
                                        >
                                            Bulk Upload
                                        </mui.Button>
                                        <mui.Button 
                                            variant="contained" 
                                            onClick={createRisk}
                                            disabled={!formValid || isLoading}
                                            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
                                            size="small"
                                            sx={{
                                                py: 1,
                                                px: 2,
                                                borderRadius: '4px',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: '0.875rem',
                                                boxShadow: 'none',
                                                backgroundColor: 'primary.main',
                                                '&:hover': {
                                                    backgroundColor: 'primary.dark',
                                                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                                                },
                                                '&:disabled': {
                                                    backgroundColor: isLoading ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                                                    color: isLoading ? 'white' : 'rgba(0, 0, 0, 0.26)',
                                                    opacity: isLoading ? 0.7 : 1
                                                }
                                            }}
                                        >
                                            {isLoading ? 'Creating...' : 'Create Risk'}
                                        </mui.Button>
                                    </mui.Stack>
                                </mui.Box>
                            </mui.Box>
                        </mui.Box>
                    </DynamicDrawer>

                    <CustomSpeedDial 
                        actions={[
                            { 
                                name: 'Create Risk', 
                                icon: <BugReportIcon />, 
                                onClick: () => toggleDrawer(true)() 
                            }
                        ]} 
                    />

                    <DynamicSnackbar
                        open={snackbarOpen}
                        message={snackbarMessage}
                        severity={snackbarSeverity}
                        onClose={handleSnackbarClose}
                    />

                    <Modal
                        open={openRisksModal}
                        size={'lg'}
                        onClose={handleCloseRisksModal}
                        body={
                            <div>
                                <mui.Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center',
                                    mb: 2
                                }}>
                                    <mui.Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Bulk Upload Risk Library
                                    </mui.Typography>
                                    <mui.Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadTemplate}
                                        sx={{
                                            borderRadius: '4px',
                                            textTransform: 'none',
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        Download Template
                                    </mui.Button>
                                </mui.Box>
                                <mui.Box sx={{ 
                                    mb: 2, 
                                    p: 1.5, 
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    borderLeft: '3px solid',
                                    borderColor: 'primary.main',
                                    borderRadius: '2px'
                                }}>
                                    <mui.Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>
                                        Upload a CSV file with risk data. The file should contain these required columns: RISK_ID, RISK_NAME, RISK_TYPE, and RISK_DESCRIPTION.
                                        <br /><br />
                                        <strong>Note:</strong> COMPANY_ID and REQUIRED columns are optional. If COMPANY_ID is not specified, your current company ID will be automatically assigned to each risk.
                                    </mui.Typography>
                                </mui.Box>
                                
                                {isUploadComplete ? (
                                    <mui.Box sx={{ 
                                        textAlign: 'center', 
                                        p: 3, 
                                        backgroundColor: 'rgba(76, 175, 80, 0.08)',
                                        borderRadius: '4px',
                                        mb: 2
                                    }}>
                                        <CloudDoneIcon sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
                                        <mui.Typography variant="h6" color="success.main">
                                            Upload Complete!
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Your risks have been successfully uploaded.
                                        </mui.Typography>
                                    </mui.Box>
                                ) : isLoading ? (
                                    <mui.Box sx={{ 
                                        textAlign: 'center', 
                                        p: 3, 
                                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                        borderRadius: '4px',
                                        mb: 2
                                    }}>
                                        <CircularProgress size={48} sx={{ mb: 1 }} />
                                        <mui.Typography variant="h6" color="primary">
                                            Uploading...
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Please wait while your risks are being processed.
                                        </mui.Typography>
                                    </mui.Box>
                                ) : null}
                                
                                <FileUploadRisks onRisksDataChange={handleOnRisksData} />
                                
                                {risksUpload && risksUpload.length > 0 && !isLoading && !isUploadComplete && (
                                    <mui.Box sx={{ 
                                        textAlign: 'center', 
                                        p: 2, 
                                        mt: 2,
                                        backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                        borderRadius: '4px' 
                                    }}>
                                        <mui.Typography variant="body1" color="primary">
                                            {risksUpload.length} risks ready to upload
                                        </mui.Typography>
                                        <mui.Typography variant="body2" color="text.secondary">
                                            Click the Upload button to proceed
                                        </mui.Typography>
                                    </mui.Box>
                                )}
                            </div>
                        }
                        footer={
                            <mui.Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', p: 2 }}>
                                <mui.Button 
                                    onClick={handleCloseRisksModal}
                                    variant="outlined"
                                    sx={{
                                        borderColor: 'rgba(25, 118, 210, 0.08)',
                                        color: 'text.primary',
                                        '&:hover': {
                                            borderColor: 'rgba(25, 118, 210, 0.12)',
                                            backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                        }
                                    }}
                                >
                                    {isUploadComplete ? 'Close' : 'Cancel'}
                                </mui.Button>
                                <LoadingButton
                                    onClick={handleButtonClick}
                                    loading={isLoading}
                                    loadingPosition="start"
                                    startIcon={isUploadComplete ? <CloudDoneIcon /> : <CloudUploadIcon />}
                                    variant="contained"
                                    color="primary"
                                    disabled={(!risksUpload || risksUpload.length === 0) || isUploadComplete}
                                    sx={{
                                        '&.Mui-disabled': { 
                                            color: isUploadComplete ? 'white' : 'rgba(255, 255, 255, 0.7)', 
                                            backgroundColor: isUploadComplete ? 'success.main' : 'primary.main',
                                            opacity: isUploadComplete ? 1 : 0.7
                                        }
                                    }}
                                >
                                    {isLoading ? 'Uploading...' : isUploadComplete ? 'Upload Complete' : 'Upload'}
                                </LoadingButton>
                            </mui.Stack>
                        }
                    />
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    {/* Edit Risk Dialog */}
    const editRiskModal = (
        <Modal
            open={editModalOpen}
            size={'md'}
            onClose={handleEditModalClose}
            body={
                <div>
                    <mui.Box sx={{ 
                        p: 1,
                        mb: 3
                    }}>
                        <mui.Typography variant="h6" sx={{ 
                            fontWeight: 600, 
                            color: 'text.primary',
                            position: 'relative',
                            '&:after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                left: 0,
                                width: '40px',
                                height: '3px',
                                backgroundColor: 'primary.main'
                            }
                        }}>
                            Edit Risk
                        </mui.Typography>
                    </mui.Box>

                    {editingRisk && (
                        <mui.Box component="form" noValidate autoComplete="off" sx={{ mt: 2 }}>
                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Risk ID <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <NormalTextField
                                    error={!!editErrors.RISK_ID}
                                    name="RISK_ID"
                                    placeholder="Enter risk identifier"
                                    value={editingRisk.RISK_ID || ''}
                                    onChange={handleEditChange}
                                    helperText={editErrors.RISK_ID || ' '}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        mt: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            height: '40px',
                                        }
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Risk Name <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <NormalTextField
                                    error={!!editErrors.RISK_NAME}
                                    name="RISK_NAME"
                                    placeholder="Enter descriptive name"
                                    value={editingRisk.RISK_NAME || ''}
                                    onChange={handleEditChange}
                                    helperText={editErrors.RISK_NAME || ' '}
                                    fullWidth
                                    size="small"
                                    sx={{
                                        mt: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                            height: '40px',
                                        }
                                    }}
                                />
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Risk Type <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <MultipleSelect
                                    isMultiSelect={false}
                                    defaultValue={editRiskType}
                                    placeholderText="Select risk type"
                                    selectOptions={riskTypeOptions}
                                    selectedOptions={editRiskType}
                                    handleChange={handleEditRiskTypeChange}
                                    error={!!editErrors.RISK_TYPE}
                                    customStyles={{
                                        control: (base) => ({
                                            ...base,
                                            minHeight: '40px',
                                            height: '40px',
                                            fontSize: '0.875rem'
                                        })
                                    }}
                                />
                                {editErrors.RISK_TYPE && (
                                    <mui.Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                        {editErrors.RISK_TYPE}
                                    </mui.Typography>
                                )}
                            </mui.Box>

                            <mui.Box sx={{ mb: 2.5 }}>
                                <mui.Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, color: 'text.secondary', fontSize: '0.85rem' }}>
                                    Risk Description <mui.Typography component="span" color="error" sx={{ fontSize: '0.85rem' }}>*</mui.Typography>
                                </mui.Typography>
                                <NormalTextField
                                    error={!!editErrors.RISK_DESCRIPTION}
                                    name="RISK_DESCRIPTION"
                                    placeholder="Enter detailed description of the risk"
                                    value={editingRisk.RISK_DESCRIPTION || ''}
                                    onChange={handleEditChange}
                                    helperText={editErrors.RISK_DESCRIPTION || ' '}
                                    isMultiLine={true}
                                    rows="4"
                                    fullWidth
                                    sx={{
                                        mt: 0,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '4px',
                                        }
                                    }}
                                />
                            </mui.Box>
                        </mui.Box>
                    )}
                </div>
            }
            footer={
                <mui.Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', p: 2 }}>
                    <mui.Button 
                        onClick={handleEditModalClose}
                        variant="outlined"
                        sx={{
                            borderColor: 'rgba(25, 118, 210, 0.08)',
                            color: 'text.primary',
                            '&:hover': {
                                borderColor: 'rgba(25, 118, 210, 0.12)',
                                backgroundColor: 'rgba(25, 118, 210, 0.04)'
                            }
                        }}
                    >
                        Cancel
                    </mui.Button>
                    <mui.Button 
                        variant="contained" 
                        onClick={updateRisk}
                        disabled={!editFormValid || editIsLoading}
                        startIcon={editIsLoading ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{
                            py: 1,
                            px: 2,
                            borderRadius: '4px',
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            boxShadow: 'none',
                            backgroundColor: 'primary.main',
                            '&:hover': {
                                backgroundColor: 'primary.dark',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                            },
                            '&:disabled': {
                                backgroundColor: editIsLoading ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                                color: editIsLoading ? 'white' : 'rgba(0, 0, 0, 0.26)',
                                opacity: editIsLoading ? 0.7 : 1
                            }
                        }}
                    >
                        {editIsLoading ? 'Updating...' : 'Update Risk'}
                    </mui.Button>
                </mui.Stack>
            }
        />
    );

    return (
        <Suspense fallback={renderLoadingState()}>
            <div>
                <IASideBar mainContent={customMainContent} />
                {editRiskModal}
                <mui.Dialog
                    open={deleteConfirmOpen}
                    onClose={handleDeleteConfirmClose}
                    PaperProps={{
                        sx: {
                            borderRadius: '8px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                            maxWidth: '400px',
                            width: '100%'
                        }
                    }}
                >
                    <mui.Box sx={{ pt: 2, px: 2 }}>
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <DeleteIcon sx={{ color: 'error.main', mr: 1.5, fontSize: '22px' }} />
                            <mui.Typography variant="h6" sx={{ 
                                fontWeight: 600,
                                fontSize: '1.1rem',
                                color: 'text.primary'
                            }}>
                                Delete Risk
                            </mui.Typography>
                        </mui.Box>
                        <mui.Divider sx={{ mt: 1.5, mb: 2 }} />
                        
                        <mui.Box sx={{ px: 1, pb: 2 }}>
                            <mui.Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                Are you sure you want to delete this risk?
                            </mui.Typography>
                            
                            <mui.Box sx={{ 
                                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                p: 1.5,
                                borderRadius: '4px',
                                mt: 2,
                                mb: 1
                            }}>
                                <mui.Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {riskToDelete?.RISK_ID || ''}
                                </mui.Typography>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {riskToDelete?.RISK_NAME || ''}
                                </mui.Typography>
                            </mui.Box>
                            
                            <mui.Typography 
                                variant="body2" 
                                color="error.main" 
                                sx={{ 
                                    mt: 2, 
                                    display: 'flex', 
                                    alignItems: 'center'
                                }}
                            >
                                <WarningAmberRoundedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                This action cannot be undone
                            </mui.Typography>
                            
                            <mui.Box sx={{ 
                                mt: 2, 
                                p: 1.5, 
                                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                borderLeft: '3px solid',
                                borderColor: 'warning.main',
                                borderRadius: '2px'
                            }}>
                                <mui.Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.85rem', display: 'flex', alignItems: 'center' }}>
                                    <WarningAmberRoundedIcon sx={{ fontSize: 16, mr: 0.5, color: 'warning.main' }} />
                                    Note: Risks that are referenced in audits cannot be deleted until all references are removed.
                                </mui.Typography>
                            </mui.Box>
                        </mui.Box>
                    </mui.Box>

                    <mui.DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
                        <mui.Button 
                            onClick={handleDeleteConfirmClose} 
                            variant="outlined"
                            disabled={deleteIsLoading}
                            sx={{
                                borderColor: 'rgba(0, 0, 0, 0.12)',
                                color: 'text.primary',
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderColor: 'rgba(0, 0, 0, 0.2)'
                                }
                            }}
                        >
                            Cancel
                        </mui.Button>
                        <LoadingButton
                            onClick={handleDeleteConfirm}
                            variant="contained"
                            loading={deleteIsLoading}
                            sx={{ 
                                textTransform: 'none',
                                backgroundColor: 'error.main',
                                '&:hover': {
                                    backgroundColor: 'error.dark'
                                },
                                '&.Mui-disabled': { 
                                    color: 'rgba(255, 255, 255, 0.7)', 
                                    backgroundColor: 'error.main',
                                    opacity: 0.7
                                } 
                            }}
                        >
                            {deleteIsLoading ? 'Deleting...' : 'Delete Risk'}
                        </LoadingButton>
                    </mui.DialogActions>
                </mui.Dialog>

                {/* Dependency Alert Dialog */}
                {dependencyInfo && (
                    <mui.Dialog
                        open={!!dependencyInfo}
                        onClose={() => setDependencyInfo(null)}
                        PaperProps={{
                            sx: {
                                borderRadius: '8px',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                maxWidth: '450px',
                                width: '100%'
                            }
                        }}
                    >
                        <mui.Box sx={{ pt: 2, px: 2 }}>
                            <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <WarningAmberRoundedIcon sx={{ color: 'warning.main', mr: 1.5, fontSize: '22px' }} />
                                <mui.Typography variant="h6" sx={{ 
                                    fontWeight: 600,
                                    fontSize: '1.1rem',
                                    color: 'text.primary'
                                }}>
                                    Cannot Delete Risk
                                </mui.Typography>
                            </mui.Box>
                            <mui.Divider sx={{ mt: 1.5, mb: 2 }} />
                            
                            <mui.Box sx={{ px: 1, pb: 2 }}>
                                <mui.Typography variant="body1" sx={{ mb: 2 }}>
                                    The risk <strong>{dependencyInfo.riskId}</strong> - "{dependencyInfo.riskName}" cannot be deleted because it is referenced in {dependencyInfo.affectedTable}.
                                </mui.Typography>
                                
                                <mui.Box sx={{ 
                                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                                    p: 2,
                                    borderRadius: '4px',
                                    mb: 2,
                                    borderLeft: '3px solid',
                                    borderColor: 'primary.main',
                                }}>
                                    <mui.Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                        To delete this risk, you need to:
                                    </mui.Typography>
                                    <mui.Box component="ol" sx={{ ml: 2, mt: 0, mb: 0, pl: 0 }}>
                                        <mui.Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                            Find all audit files using this risk
                                        </mui.Typography>
                                        <mui.Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                            Remove the references to this risk in those audits
                                        </mui.Typography>
                                        <mui.Typography component="li" variant="body2">
                                            Try deleting the risk again
                                        </mui.Typography>
                                    </mui.Box>
                                </mui.Box>
                            </mui.Box>
                        </mui.Box>

                        <mui.DialogActions sx={{ px: 2, pb: 2, pt: 0 }}>
                            <mui.Button 
                                onClick={() => setDependencyInfo(null)} 
                                variant="contained"
                                color="primary"
                                sx={{
                                    textTransform: 'none',
                                }}
                            >
                                I Understand
                            </mui.Button>
                        </mui.DialogActions>
                    </mui.Dialog>
                )}
            </div>
        </Suspense>
    );



}
export default AuditRiskLibrary;